import { useMemo, useRef } from 'react'

import { ButtonText } from '@/components/common/Button'
import { LoadingMask } from '@/components/common/LoadingMask'
import { NoResults } from '@/components/common/NoResults'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { DecklistExport } from '@/components/layout/Decklist/DecklistExport/DecklistExport'
import { usePriceSources } from '@/hooks/usePriceSources'
import { clipboardActions } from '@/store/clipboard'
import { useReduxDispatch, useReduxSelector } from '@/store/hooks'
import { selectors } from '@/store/selectors'
import { getColorId } from '@/utils/cardUtil'
import { isNonEmptyString } from '@/utils/commonUtil'
import { getGalleryResults, parseGalleryDeckText } from '@/utils/galleryUtil'

import styles from './Clipboard.module.scss'
import { ClipboardButtonRecs } from './ClipboardButtonRecs/ClipboardButtonRecs'
import { ClipboardBuyLinks } from './ClipboardBuyLinks/ClipboardBuyLinks'
import { ClipboardCard } from './ClipboardCard/ClipboardCard'
import { ClipboardGroupTitle } from './ClipboardGroupTitle/ClipboardGroupTitle'
import { useClipboard } from './hooks/useClipboard'

type Props = {
  isOpen: boolean
}

export const Clipboard = ({ isOpen }: Props) => {
  const dispatch = useReduxDispatch()
  const isRehydrated = useReduxSelector(selectors.isRehydrated)
  const hasHistory = useReduxSelector((s) => !!s.clipboard.prevStates.length)
  const { cardIds, cardMap, loading: loadingCards, recs, recsPending } = useClipboard({ enabled: isOpen })
  const priceSource = usePriceSources()[0]
  const contentRef = useRef<HTMLDivElement>(null)
  const recsPendingRef = useRef<HTMLLIElement>(null)
  const decklist = useMemo(() => parseGalleryDeckText(recs.filter(({ name }) => isNonEmptyString(name))), [recs])

  const { commanderColorId, groups } = useMemo(() => {
    if (!isOpen) return { groups: [] }
    const { groups } = getGalleryResults({
      cardMap,
      categories: [],
      groupby: 'type',
      priceSource,
      recs,
      sortby: 'name',
      sortbyDirection: 'desc',
    })

    const commanders = groups.find((g) => g.slug === 'type-commander')?.recs
    const commanderColorIds = commanders?.map((r) => cardMap[r.cardId]?.colorId).filter(isNonEmptyString)
    const commanderColorId = commanderColorIds?.length ? getColorId(commanderColorIds) : undefined

    return { commanderColorId, groups }
  }, [isOpen, cardMap, priceSource, recs])

  const onSuccessRecs = () => {
    requestAnimationFrame(() => {
      const content = contentRef.current
      const recs = recsPendingRef.current
      if (!content || !recs) return
      content.scrollTo({ behavior: 'smooth', top: recs.offsetTop })
    })
  }

  const onClickUndo = () => dispatch(clipboardActions.undo())

  const onClickClear = () => dispatch(clipboardActions.clear())

  const onClickRemoveRecs = () => {
    dispatch(clipboardActions.clearPending())
  }

  const isEmpty = !recs.length
  const isEmptyAll = isEmpty && !recsPending.length
  const loading = loadingCards || !isRehydrated

  if (!isOpen) return null

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        <ButtonText disabled={!hasHistory} onClick={onClickUndo} size='sm'>
          <IconWithLabel iconBefore='rotateLeft' iconBeforeSize='sm'>
            Undo
          </IconWithLabel>
        </ButtonText>
        <DecklistExport analyticsCategory='clipboard' decklist={decklist} disabled={isEmpty} recs={recs}>
          <ButtonText disabled={isEmpty} size='sm'>
            <IconWithLabel iconBefore='share' iconBeforeSize='sm'>
              Export
            </IconWithLabel>
          </ButtonText>
        </DecklistExport>
        <ClipboardButtonRecs loading={loading} onSuccess={onSuccessRecs} recs={recs} />
        <ButtonText disabled={isEmptyAll} hover='warning' onClick={onClickClear} size='sm'>
          <IconWithLabel iconBefore='trash' iconBeforeSize='sm'>
            Clear
          </IconWithLabel>
        </ButtonText>
      </div>
      <div className={styles.content} ref={contentRef}>
        {!isRehydrated ? null : !groups.length && !recsPending.length ? (
          <NoResults message='No cards added to clipboard yet!' />
        ) : (
          <ul className={styles.list}>
            {groups.map((group, i) => (
              <li className={styles.group} key={i}>
                <ClipboardGroupTitle
                  count={group.recs.reduce((acc, rec) => acc + (rec.count || 1), 0)}
                  icon={group.icon}
                  label={group.label}
                />
                <ul className={styles.groupCards}>
                  {group.recs.map((rec) => (
                    <li key={rec.cardId}>
                      <ClipboardCard
                        cardMap={cardMap}
                        commanderColorId={commanderColorId}
                        rec={rec}
                        variant='clipboard'
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {!!recsPending.length && (
              <li className={styles.group} ref={recsPendingRef}>
                <ClipboardGroupTitle count={recsPending.length} icon='chartSimple' label='Recs'>
                  <ButtonText aria-label='Clear all Recs' onClick={onClickRemoveRecs}>
                    <Icon name='trash' />
                  </ButtonText>
                </ClipboardGroupTitle>
                <ul className={styles.groupCards}>
                  {recsPending.map((rec) => (
                    <li key={rec.cardId}>
                      <ClipboardCard
                        cardMap={cardMap}
                        commanderColorId={commanderColorId}
                        rec={rec}
                        variant='pending'
                      />
                    </li>
                  ))}
                </ul>
              </li>
            )}
          </ul>
        )}
      </div>
      <ClipboardBuyLinks cardIds={cardIds} cardMap={cardMap} decklist={decklist} />
      <LoadingMask loading={loading} />
    </div>
  )
}
